import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { 
  useSalonsCategoriesListQuery, 
  useAuthProfileRetrieveQuery,
  useSalonsListQuery 
} from "../../../../services/GLOWClient"
import {
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  AvatarGroup,
} from "@mui/material"
import { Edit as EditIcon, Delete as DeleteIcon, Business as BusinessIcon, Store as StoreIcon, Translate as TranslateIcon } from "@mui/icons-material"
import theme from "../../../../theme"
import AddCategory from "./addCategory"
import UpdateCategory from "./updateCategory"
import DeleteCategory from "./deleteCategory"
import TranslationValidationModal from "../TranslationValidationModal"

const Category = ({ salonId, onSuccess }) => {
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [expandedDescriptions, setExpandedDescriptions] = useState({})
  const [translationModalOpen, setTranslationModalOpen] = useState(false)
  const [translationModalLoading, setTranslationModalLoading] = useState(false)
  const [translationCategory, setTranslationCategory] = useState(null)
  const [categoryTranslations, setCategoryTranslations] = useState(null)
  const { t, i18n } = useTranslation();

  // Fetch user profile to get owner ID
  const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
  
  // Fetch salons data filtered by owner
  const { data: salons, isLoading: salonsLoading } = useSalonsListQuery(
    userProfile ? { owner: userProfile.id } : { owner: undefined },
    { skip: !userProfile }
  );

  // Fetch categories data for the specific salon
  const { data: allCategoriesData, error, isLoading: categoriesLoading, refetch } = useSalonsCategoriesListQuery(
    salonId ? { salonId: Number(salonId), language: i18n.language } : {},
    { skip: !salonId }
  )

  // Combined loading state
  const isLoading = profileLoading || salonsLoading || categoriesLoading;

  // Since we're fetching salon-specific categories, use them directly
  const categoriesData = React.useMemo(() => {
    if (!allCategoriesData) {
      return [];
    }
    // allCategoriesData is already filtered by salon from the API
    return Array.isArray(allCategoriesData) ? allCategoriesData : [];
  }, [allCategoriesData]);

  const currentLanguage = (i18n.language || "fr").split("-")[0].toLowerCase()

  const getTranslatedField = React.useCallback((item, field) => {
    const translationsKey = `${field}_translations`
    const localizedKey = `localized_${field}`
    const translations = item?.[translationsKey] || {}

    if (translations[currentLanguage]) {
      return translations[currentLanguage]
    }

    if (item?.[localizedKey]) {
      return item[localizedKey]
    }

    for (const fallbackLanguage of ["fr", "en", "ar"]) {
      if (translations[fallbackLanguage]) {
        return translations[fallbackLanguage]
      }
    }

    return item?.[field] || (field === "description" ? "-" : "")
  }, [currentLanguage]);

  const handleDelete = (category) => {
    // For grouped categories, we need to handle deletion differently
    // For now, we'll pass the first instance, but you might want to show
    // a dialog asking which salon to remove from
    setSelectedCategory(category.instances ? category.instances[0] : category)
    setIsDeleteDialogOpen(true)
  }

  const handleEdit = (category) => {
    // Similar to delete, handle grouped categories
    setSelectedCategory(category.instances ? category.instances[0] : category)
    setIsUpdateDialogOpen(true)
  }

  const handleOpenTranslations = async (category) => {
    const targetCategory = category.instances ? category.instances[0] : category
    setTranslationCategory(targetCategory)
    setTranslationModalOpen(true)
    setTranslationModalLoading(true)
    setCategoryTranslations(null)

    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
      const categoryId = targetCategory.category_id || targetCategory.id
      const response = await fetch(`/api/categories/${categoryId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setCategoryTranslations({
          name: data.name_translations || {},
          description: data.description_translations || {},
        })
      }
    } catch (e) {
      console.warn('[category translations] fetch error', e)
    } finally {
      setTranslationModalLoading(false)
    }
  }

  const handleSaveCategoryTranslations = async (editedTranslations) => {
    setTranslationModalOpen(false)
    if (!translationCategory) return

    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
      const categoryId = translationCategory.category_id || translationCategory.id
      await fetch(`/api/categories/${categoryId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name_translations: editedTranslations.name,
          description_translations: editedTranslations.description,
        }),
      })
      refetch()
    } catch (e) {
      console.warn('[category translations] patch error', e)
    }
  }

  // Group categories by name and aggregate salon information
  const groupedData = React.useMemo(() => {
    if (!categoriesData) {
      return []

    }
    if (!Array.isArray(categoriesData)) {
      console.error("Categories data is not an array:", categoriesData)
      return []
    }

    // Create a map to group categories by original name (language-invariant key)
    const categoryGroups = new Map();

    categoriesData.forEach((category) => {
      const displayName = getTranslatedField(category, "name")
      const displayDescription = getTranslatedField(category, "description")
      const categoryKey = category.name?.toLowerCase() || 'unnamed';
      
      if (!categoryGroups.has(categoryKey)) {
        categoryGroups.set(categoryKey, {
          id: `group_${categoryKey}`,
          name: displayName,
          description: displayDescription,
          salons: [],
          instances: [],
          isGrouped: false,
          is_default: Boolean(category.is_default)
        });
      }

      const group = categoryGroups.get(categoryKey);
      // Update localized name/description in case language changed
      group.name = displayName;
      group.description = displayDescription;
      group.is_default = group.is_default && Boolean(category.is_default);
      
      // Add salon info if not already present
      const salonInfo = {
        salon_id: category.salon,

        salon_name: category.salon_name || "-"
      };
      
      if (!group.salons.find(s => s.salon_id === salonInfo.salon_id)) {
        group.salons.push(salonInfo);
      }
      
      // Store the original category instance with all translation fields
      group.instances.push({
        id: category.category_id || 0,
        category_id: category.category_id || 0,
        name: displayName,
        name_translations: category.name_translations || {},
        description_translations: category.description_translations || {},
        salon: category.salon || 0,
        salon_name: category.salon_name || "-",
        description: displayDescription,
        is_default: Boolean(category.is_default),
      });
      
      // Mark as grouped if more than one salon uses this category
      group.isGrouped = group.salons.length > 1;
    });

    return Array.from(categoryGroups.values());
  }, [categoriesData, getTranslatedField]);

  const toggleExpand = (id) => {
    setExpandedDescriptions((prev) => ({
      ...prev,

      [id]: !prev[id],
    }))
  }

  // Helper function to get salon color for chips
  const getSalonColor = (index) => {
    const colors = [
      theme.colors.primary,
      theme.colors.secondary,
      { 100: '#E3F2FD', 600: '#1565C0', 700: '#0D47A1' }, // Blue
      { 100: '#F3E5F5', 600: '#8E24AA', 700: '#4A148C' }, // Purple
      { 100: '#E8F5E8', 600: '#2E7D32', 700: '#1B5E20' }, // Green
      { 100: '#FFF3E0', 600: '#F57C00', 700: '#E65100' }, // Orange
    ];
    return colors[index % colors.length];
  };

  return (
    <>
      {/* Enhanced Container with gradient background */}
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%)`,
          padding: "32px",
          "@media (max-width: 768px)": {
            padding: "16px",
          },
        }}
      >
      
        {/* Main Content Area */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "20px",
            border: `1px solid ${theme.colors.border.light}`,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            padding: "24px",
            minHeight: "300px",
            "@media (max-width: 768px)": {
              padding: "16px",
            },
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "60px 0",
                color: theme.colors.text.secondary,
              }}
            >
              <CircularProgress
                size={40}
                sx={{
                  color: theme.colors.primary[500],
                  marginBottom: "12px",
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Chargement des catégories...
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                padding: "24px",
                backgroundColor: theme.colors.error[50],
                color: theme.colors.error[700],
                borderRadius: "12px",
                border: `1px solid ${theme.colors.error[200]}`,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: "6px" }}>
                Erreur lors du chargement
              </Typography>
              <Typography variant="body2">{error?.message || "Une erreur inconnue s'est produite"}</Typography>
            </Box>
          ) : !groupedData || groupedData.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                padding: "60px 20px",
                color: theme.colors.text.secondary,
              }}
            >
              <Box
                sx={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: theme.colors.grey[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <BusinessIcon sx={{ fontSize: "30px", color: theme.colors.grey[400] }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  marginBottom: "6px",
                  color: theme.colors.text.primary,
                }}
              >
                Aucune catégorie disponible pour vos salons
              </Typography>
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                {!userProfile && "Veuillez vous connecter pour voir vos catégories."}
                {userProfile && (!salons || salons.length === 0) && "Vous devez d'abord créer un salon pour pouvoir ajouter des catégories."}
                {userProfile && salons && salons.length > 0 && "Commencez par ajouter votre première catégorie"}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Enhanced Section Header */}
              <Box sx={{ marginBottom: "24px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: theme.colors.primary[600],
                      fontSize: "20px",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Catégories
                  </Typography>
                  <Chip
                    label={`${groupedData.length} catégorie${groupedData.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.primary[100],
                      color: theme.colors.primary[700],
                      fontWeight: 600,
                      fontSize: "11px",
                      height: "22px",
                    }}
                  />
                  <Chip
                    label={`${categoriesData.length} instance${categoriesData.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.secondary[100],
                      color: theme.colors.secondary[700],
                      fontWeight: 600,
                      fontSize: "11px",
                      height: "22px",
                    }}
                  />
                </Box>
                <Divider
                  sx={{
                    borderColor: theme.colors.border.light,
                    borderWidth: "1px",
                  }}
                />
              </Box>

              {/* Improved Cards Grid with Grouping */}
              <Grid container spacing={2}>
                {groupedData.map((category) => (
                  <Grid item xs={12} sm={6} md={4} xl={3} key={category.id}>
                    <Card
                      sx={{
                        width: "100%",
                        height: "240px", // Increased height to accommodate salon info
                        display: "flex",
                        flexDirection: "column",
                        background: "linear-gradient(145deg, #ffffff 0%, #fdfdfd 100%)",
                        border: `1px solid ${theme.colors.border.light}`,
                        borderRadius: "12px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "3px",
                          background: category.isGrouped
                            ? `linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.secondary[300]}, #1565C0)`
                            : `linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.secondary[300]})`,
                        },
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: `0 12px 28px rgba(65, 101, 72, 0.12)`,
                          borderColor: theme.colors.primary[200],
                          "& .category-actions": {
                            opacity: 1,
                            transform: "translateY(0)",
                          },
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          padding: "16px",
                          gap: "12px",
                          "&:last-child": {
                            paddingBottom: "16px",
                          },
                        }}
                      >
                        {/* Category Header with Multi-salon Indicator */}
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: theme.colors.primary[700],
                                fontSize: "16px",
                                lineHeight: "1.2",
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                flexGrow: 1,
                              }}
                            >
                              {category.localized_name || category.name}
                            </Typography>
                            {category.isGrouped && (
                              <Chip
                                label="Multi-salon"
                                size="small"
                                sx={{
                                  backgroundColor: '#E3F2FD',
                                  color: '#1565C0',
                                  fontWeight: 600,
                                  fontSize: "10px",
                                  height: "18px",
                                  minWidth: "auto",
                                }}
                              />
                            )}
                          </Box>

                          {/* Salon Chips or Grouped Display */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            <StoreIcon
                              sx={{
                                fontSize: "14px",
                                color: theme.colors.secondary[600],
                                flexShrink: 0,
                              }}
                            />
                            
                            {category.salons.length <= 2 ? (
                              // Show individual chips for 1-2 salons
                              category.salons.map((salon, index) => (
                                <Chip
                                  key={salon.salon_id}
                                  label={salon.salon_name}
                                  size="small"
                                  sx={{
                                    backgroundColor: getSalonColor(index)[100],
                                    color: getSalonColor(index)[700],
                                    fontWeight: 500,
                                    fontSize: "11px",
                                    height: "20px",
                                  }}
                                />
                              ))
                            ) : (
                              // Show avatar group + count for 3+ salons
                              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: 10 } }}>
                                  {category.salons.slice(0, 3).map((salon, index) => (
                                    <Avatar
                                      key={salon.salon_id}
                                      sx={{
                                        bgcolor: getSalonColor(index)[600],
                                        width: 20,
                                        height: 20,
                                        fontSize: 10,
                                      }}
                                    >
                                      {salon.salon_name.charAt(0).toUpperCase()}
                                    </Avatar>
                                  ))}
                                </AvatarGroup>
                                <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                                  {category.salons.length} salons
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Description Section */}
                        <Box
                          sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.colors.text.secondary,
                              lineHeight: "1.4",
                              fontSize: "13px",
                              textAlign: "justify",
                              textJustify: "inter-word",
                              width: "100%",
                              wordBreak: "break-word",
                              hyphens: "auto",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: "unset",
                              maxHeight: "60px", // Reduced due to salon info taking more space
                              minHeight: "40px",
                            }}
                          >
                            {category.description && category.description !== "-"
                              ? category.description
                              : "Aucune description disponible"}
                          </Typography>
                        </Box>

                        {!category.is_default && (
                          <Button
                            onClick={() => handleOpenTranslations(category)}
                            startIcon={<TranslateIcon sx={{ fontSize: '18px !important' }} />}
                            variant="outlined"
                            fullWidth
                            sx={{
                              borderRadius: '10px',
                              borderColor: theme.colors.secondary[200],
                              backgroundColor: theme.colors.secondary[50],
                              color: theme.colors.secondary[700],
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              '&:hover': {
                                borderColor: theme.colors.secondary[300],
                                backgroundColor: theme.colors.secondary[100],
                              },
                            }}
                          >
                            {t('pro_salon.translations.verify_translations', 'Vérifier traduction')}
                          </Button>
                        )}

                        {/* Action Buttons with Instance Selection */}
                        <Box
                          className="category-actions"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "auto",
                            opacity: 1,
                            transform: "none",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {/* Instance selector for grouped categories */}
                          {category.isGrouped && (
                            <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                              {category.instances.map((instance, index) => (
                                <Tooltip 
                                  key={instance.id}
                                  title={`${instance.salon_name} - Actions disponibles`}
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: "2px",
                                      padding: "2px",
                                      borderRadius: "4px",
                                      backgroundColor: getSalonColor(index)[100],
                                      border: `1px solid ${getSalonColor(index)[200] || getSalonColor(index)[100]}`,
                                    }}
                                  >
                                    <IconButton
                                      onClick={() => handleEdit(category, instance)}
                                      size="small"
                                      sx={{
                                        padding: "2px",
                                        minWidth: "20px",
                                        height: "20px",
                                        borderRadius: "2px",
                                        "&:hover": {
                                          backgroundColor: getSalonColor(index)[200] || theme.colors.primary[100],
                                        },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: "12px", color: getSalonColor(index)[700] }} />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => handleDelete(category, instance)}
                                      size="small"
                                      sx={{
                                        padding: "2px",
                                        minWidth: "20px",
                                        height: "20px",
                                        borderRadius: "2px",
                                        "&:hover": {
                                          backgroundColor: theme.colors.error[100],
                                        },
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: "12px", color: theme.colors.error[500] }} />
                                    </IconButton>
                                  </Box>
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                          
                          {/* Standard action buttons for single instances */}
                          {!category.isGrouped && (
                            <Box sx={{ display: "flex", gap: "2px" }}>
                              <Tooltip title="Modifier" arrow>
                                <IconButton
                                  onClick={() => handleEdit(category)}
                                  sx={{
                                    padding: "6px",
                                    borderRadius: "6px",
                                    backgroundColor: "transparent",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: theme.colors.primary[50],
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                >
                                  <EditIcon
                                    sx={{
                                      fontSize: "16px",
                                      color: theme.colors.primary[600],
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Supprimer" arrow>
                                <IconButton
                                  onClick={() => handleDelete(category)}
                                  sx={{
                                    padding: "6px",
                                    borderRadius: "6px",
                                    backgroundColor: "transparent",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: theme.colors.error[50],
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                >
                                  <DeleteIcon
                                    sx={{
                                      fontSize: "16px",
                                      color: theme.colors.error[500],
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </Box>

      {/* Dialogs remain the same */}
        <AddCategory
          open={isAddCategoryDialogOpen}
          onClose={() => setIsAddCategoryDialogOpen(false)}
          onSuccess={() => {
            setIsAddCategoryDialogOpen(false)
            refetch()
          }}
          selectedSalonId={salonId}
        />

      {selectedCategory && (
        <UpdateCategory
          open={isUpdateDialogOpen}
          onClose={() => setIsUpdateDialogOpen(false)}
          onSuccess={() => {
            setIsUpdateDialogOpen(false)
            refetch()
          }}
          categoryData={selectedCategory}
          salonId={salonId}
        />
      )}

      {selectedCategory && (
        <DeleteCategory
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onSuccess={() => {
            setIsDeleteDialogOpen(false)
            refetch()
          }}
          categoryData={selectedCategory}
        />
      )}

      <TranslationValidationModal
        open={translationModalOpen}
        onClose={() => setTranslationModalOpen(false)}
        onValidate={handleSaveCategoryTranslations}
        salonName={translationCategory?.name || ''}
        salonDescription={translationCategory?.description || ''}
        isLoading={translationModalLoading}
        translations={categoryTranslations}
      />
    </>
  )
}

export default Category